import { useEffect, useState } from 'react'
import { fetchInstagramThumbnail, isGenericMediaFallback } from '@/lib/instagramThumbnail'
import { MEDIA_FALLBACK_THUMB } from '@/lib/mediaUtils'
import { OptimizedImage } from '@/components/ui/OptimizedImage'
import { cn } from '@/lib/utils'

interface MediaThumbnailProps {
  src?: string | null
  alt: string
  platform?: string
  socialUrl?: string
  videoId?: number
  className?: string
}

export function MediaThumbnail({ src, alt, platform, socialUrl, videoId, className }: MediaThumbnailProps) {
  const [autoThumb, setAutoThumb] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [failedSrc, setFailedSrc] = useState<string | null>(null)

  const needsInstagramFetch =
    platform === 'instagram' &&
    Boolean(socialUrl) &&
    (!src || isGenericMediaFallback(src) || failedSrc === src)

  useEffect(() => {
    if (!needsInstagramFetch || !socialUrl) return

    let active = true
    setLoading(true)

    fetchInstagramThumbnail(socialUrl, videoId)
      .then((url) => {
        if (active) setAutoThumb(url)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [needsInstagramFetch, socialUrl, videoId, failedSrc, src])

  const resolved =
    autoThumb ||
    (src && !isGenericMediaFallback(src) && failedSrc !== src ? src : null)

  const handleError = () => {
    if (resolved && platform === 'instagram' && socialUrl) {
      setFailedSrc(resolved)
      setAutoThumb(null)
      setLoading(true)
      fetchInstagramThumbnail(socialUrl, videoId).then((url) => {
        setAutoThumb(url)
        setLoading(false)
      })
    }
  }

  if (loading && !resolved) {
    return (
      <div
        className={cn('h-full w-full animate-pulse bg-neutral-200', className)}
        aria-hidden
      />
    )
  }

  if (!resolved) {
    if (platform === 'instagram') {
      return (
        <div
          className={cn('h-full w-full bg-neutral-200', className)}
          aria-label={alt}
        />
      )
    }

    return (
      <OptimizedImage
        src={MEDIA_FALLBACK_THUMB}
        alt={alt}
        loading="lazy"
        className={cn('h-full w-full object-cover', className)}
      />
    )
  }

  return (
    <OptimizedImage
      src={resolved}
      alt={alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={handleError}
      className={cn('h-full w-full object-cover', className)}
    />
  )
}
